import React, { useState, useRef } from 'react';
import Icon1 from "../assets/Icon/Icon1.png"
import Icon2 from "../assets/Icon/Icon2.png"
import Icon3 from "../assets/Icon/Icon3.png"
import Icon5 from "../assets/Icon/Icon5.png"
import Icon6 from "../assets/Icon/Icon6.png"
import { trackCtaClicked } from '../utils/mixpanelClient';

const StickyLayoutWrapper = ({ children }) => {
  // Toggle functionality: click to open/close
  const [selectedAssistant, setSelectedAssistant] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const descriptionRefs = useRef([]);

  const aiAssistants = [
    {
      title: 'Intellix',
      description:
        'Delivers real-time sales signals and account intelligence to help you spot the right opportunities faster.',
      icon: Icon1,
      // Match accordion background colors from HowItWorksFlowchart
      bgColor: '#E8F0FF',
    },
    {
      title: 'Explorix',
      description:
        'Performs deep company and market research to give you clear, actionable insights instantly.',
      icon: Icon5,
      bgColor: '#E8F0FF',
    },
    {
      title: 'Deckly',
      description:
        'Creates templatised, high-quality AI documents like proposals, decks, and briefs in seconds.',
      icon: Icon2,
      bgColor: '#E8F0FF',
    },
    {
      title: 'Memora',
      description:
        'Builds and centralises your company’s knowledge to make experience more personalised and relevant.',
      icon: Icon3,
      bgColor: '#E8F0FF',
    },
    {
      title: 'Teamly',
      description:
        'Collaborate effortlessly with your teams. Share research, insights, and documents to close opportunities together.',
      icon: Icon6,
      bgColor: '#E8F0FF',
    },
  ];

  return (
    <section className="bg-white px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 py-3 sm:py-4 md:py-5 lg:py-3">
      <div className="max-w-7xl lg:max-w-[1200px] xl:max-w-[1300px] mx-auto flex flex-col md:flex-row gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-4 items-start">
        {/* Left Sidebar - Sticky accordion list */}
        <div
          className="w-full md:w-52 lg:w-60 xl:w-64 sticky top-20 pt-4 md:pt-6 lg:pt-8"
        >
          <div className="mb-12 sm:mb-14 md:mb-16 lg:mb-16 xl:mb-16">
            <h2
              className="text-xs sm:text-xs md:text-sm text-gray-600 font-semibold uppercase tracking-wide"
              style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
            >
              WhiteOwl AI Agents
            </h2>
            <p
              className="text-xs sm:text-xs font md:text-sm text-gray-500 mb-4 sm:mb-5 md:mb-6"
              // style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
            >
              for your every needs.
            </p>

            {/* Card wrapper to mimic the design in the screenshot */}
            <div className="rounded-2xl flex flex-col">
              {aiAssistants.map((assistant, index) => {
                const isOpen = selectedAssistant === index;
                const showChevron = isOpen || hoveredIndex === index;

                return (
                  <div
                    key={assistant.title}
                    className="rounded-[30px] transition-all duration-200 pb-2"
                    style={{
                      backgroundColor: isOpen ? assistant.bgColor : 'transparent',
                      transition: 'background-color 0.3s ease-out',
                      willChange: 'background-color, border-radius',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        const newValue = isOpen ? null : index;
                        setSelectedAssistant(newValue);
                      }}
                      onMouseEnter={() => {
                        setHoveredIndex(index);
                      }}
                      onMouseLeave={() => {
                        setHoveredIndex(null);
                      }}
                      className={`w-full flex items-center gap-2.5 sm:gap-2 px-3 py-2.5 sm:py-3 rounded-xl transition-colors duration-200 ${
                        isOpen
                          ? 'text-gray-900'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      {/* Circular image placeholder */}
                      <span
                        className={`flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-200
                          ${isOpen ? 'w-8 h-8 sm:w-8 sm:h-8 bg-[#E7F0FF]' : 'w-7 h-7 sm:w-7 sm:h-7 bg-[#F3F5F9]'}`}
                      >
                        {assistant.icon && (
                          <img
                            src={assistant.icon}
                            alt={assistant.title}
                            className={`object-contain transition-opacity duration-200 ${
                              isOpen ? 'w-4 h-4 opacity-100' : 'w-3.5 h-3.5 opacity-80'
                            }`}
                          />
                        )}
                      </span>

                      {/* Title */}
                      <span
                        className={`font-medium transition-all duration-200 ${
                          isOpen
                            ? 'text-sm sm:text-base md:text-base'
                            : 'text-xs sm:text-sm md:text-sm'
                        }`}
                        style={{
                          fontFamily: 'Bricolage Grotesque, sans-serif'
                        }}
                      >
                        {assistant.title}
                      </span>

                      {/* Chevron arrow - visible only on hover OR when open */}
                      {showChevron && (
                        <span className="ml-auto mr-3 flex items-center justify-center">
                          <svg
                            className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${
                              isOpen ? 'rotate-180 text-gray-700' : ''
                            }`}
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5 8L10 13L15 8"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </button>

                    {/* Collapsible description, expanding under the active row */}
                    <div
                      ref={(el) => {
                        descriptionRefs.current[index] = el;
                      }}
                      className={`px-3 overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-0 -translate-y-1'
                      }`}
                      style={{
                        // Use a fixed large max-height to avoid the "two-step" open animation
                        // where it first jumps to a small height then grows to content size.
                        maxHeight: isOpen ? '160px' : '0px',
                      }}
                    >
                      <div className="mt-1 mb-3 rounded-xl">
                        <p
                          className="text-[11px] sm:text-xs md:text-sm text-gray-600 leading-relaxed"
                          style={{
                            fontFamily: 'Bricolage Grotesque, sans-serif'
                          }}
                        >
                          {assistant.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
                onClick={(e) => {
                  e.preventDefault();
                  trackCtaClicked('Book a demo', { position: 'ai-agents-sidebar' });
                  window.open('https://calendly.com/thewhiteowlai/quickcall', '_blank');
                }}
                className="items-center w-fit justify-center gap-3 rounded-full mt-2 px-5 py-1.5 text-sm font-normal text-white bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-100"
              >
                Book a demo
              </button>
          </div>
        </div>

        {/* Right Content - Scrollable (Children) */}
        <div className="flex-1 flex flex-col gap-6 sm:gap-7 md:gap-8 lg:gap-4">
          {children}
        </div>
      </div>
    </section>
  );
};

export default StickyLayoutWrapper;

